import { useMemo, useRef, type TouchEvent } from "react";
import { useSearchParams } from "react-router";
import {
  addDays,
  addMonthsClamped,
  endOfMonth,
  formatDateLong,
  formatMonth,
  isDateKey,
  startOfMonth,
  startOfWeek,
  todayKey,
  weekdayShort,
  type Intervention
} from "@techplanner/shared/core";
import { useMe } from "../auth";
import { InterventionList } from "../components/domain";
import { Icon } from "../components/Icon";
import { Fab, Page, TopBar } from "../components/Layout";
import { Empty, ErrorState, IconButton, Segmented, Skeleton } from "../components/ui";
import { capitalize } from "../lib/format";
import { usePersistentState } from "../lib/hooks";
import { useInterventions, useUsers } from "../lib/queries";

type View = "day" | "week" | "month";

function byDay(items: Intervention[]): Map<string, Intervention[]> {
  const map = new Map<string, Intervention[]>();
  for (const item of items) {
    if (!item.plannedDate) continue;
    const list = map.get(item.plannedDate) ?? [];
    list.push(item);
    map.set(item.plannedDate, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => (a.startTime ?? "").localeCompare(b.startTime ?? "") || a.id - b.id);
  }
  return map;
}

function useSwipe(onLeft: () => void, onRight: () => void) {
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onTouchStart(e: TouchEvent) {
      const t = e.touches[0]!;
      start.current = { x: t.clientX, y: t.clientY };
    },
    onTouchEnd(e: TouchEvent) {
      if (!start.current) return;
      const t = e.changedTouches[0]!;
      const dx = t.clientX - start.current.x;
      const dy = t.clientY - start.current.y;
      start.current = null;
      if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.8) (dx < 0 ? onLeft : onRight)();
    }
  };
}

export function PlanningPage() {
  const me = useMe();
  const today = todayKey();
  const [params, setParams] = useSearchParams();
  const [savedView, setSavedView] = usePersistentState<View>("planning-view", "day");
  const [who, setWho] = usePersistentState<string>("planning-who", "all");
  const users = useUsers();

  const date = isDateKey(params.get("date")) ? params.get("date")! : today;
  const view: View = (["day", "week", "month"] as const).find((v) => v === params.get("view")) ?? savedView;
  const unscheduledMode = params.get("unscheduled") === "1";

  const range = useMemo(() => {
    if (view === "month") {
      const from = startOfWeek(startOfMonth(date));
      return { from, to: addDays(startOfWeek(endOfMonth(date)), 6) };
    }
    const from = startOfWeek(date);
    return { from, to: addDays(from, 6) };
  }, [date, view]);

  // Filtre par technicien : les comptes administrateur n'y figurent pas.
  const technicians = (users.data ?? []).filter((u) => u.active && u.role !== "admin");
  // Un technicien mémorisé mais désactivé (ou devenu admin) depuis : retour à toute l'équipe.
  const knownWho = who === "all" || who === "me" || !users.data || technicians.some((u) => String(u.id) === who) ? who : "all";
  const filter: Record<string, string> = knownWho === "me" ? { mine: "1" } : knownWho !== "all" ? { technicianId: knownWho } : {};
  const query = useInterventions(unscheduledMode ? { unscheduled: "1", ...filter } : { ...range, ...filter });
  const days = useMemo(() => byDay(query.data ?? []), [query.data]);

  const go = (next: Partial<{ date: string; view: View }>) => {
    const p = new URLSearchParams(params);
    if (next.date) p.set("date", next.date);
    if (next.view) {
      p.set("view", next.view);
      setSavedView(next.view);
    }
    p.delete("unscheduled");
    setParams(p, { replace: true });
  };

  const step = view === "day" ? 1 : view === "week" ? 7 : 0;
  const prev = () => go({ date: view === "month" ? addMonthsClamped(date, -1) : addDays(date, -step) });
  const next = () => go({ date: view === "month" ? addMonthsClamped(date, 1) : addDays(date, step) });
  const swipe = useSwipe(next, prev);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(date), i));
  const title = unscheduledMode ? "À planifier" : formatMonth(view === "week" || view === "day" ? weekDays[3]! : date);

  const people = [
    { value: "all", label: "Équipe" },
    { value: "me", label: "Moi" },
    ...technicians.filter((u) => u.id !== me.id).map((u) => ({ value: String(u.id), label: u.displayName }))
  ];

  return (
    <>
      <TopBar
        title={<span style={{ textTransform: "capitalize" }}>{title}</span>}
        actions={
          <>
            {!unscheduledMode && date !== today && (
              <button type="button" className="btn small soft" onClick={() => go({ date: today })}>
                Aujourd'hui
              </button>
            )}
            <IconButton
              icon="list"
              label="Interventions à planifier"
              className={unscheduledMode ? "brand" : ""}
              onClick={() => {
                const p = new URLSearchParams(params);
                if (unscheduledMode) p.delete("unscheduled");
                else p.set("unscheduled", "1");
                setParams(p, { replace: true });
              }}
            />
          </>
        }
      />
      <Page>
        <div className="stack">
          {!unscheduledMode && (
            <Segmented<View>
              label="Affichage"
              value={view}
              onChange={(v) => go({ view: v })}
              options={[
                { value: "day", label: "Jour" },
                { value: "week", label: "Semaine" },
                { value: "month", label: "Mois" }
              ]}
            />
          )}
          <div className="chips scroll" role="group" aria-label="Technicien">
            {people.map((p) => (
              <button key={p.value} type="button" className="chip" aria-pressed={knownWho === p.value} onClick={() => setWho(p.value)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {unscheduledMode ? (
          <div className="section">
            {query.isPending ? (
              <Skeleton />
            ) : (
              <InterventionList
                items={query.data ?? []}
                empty={<Empty icon="checkCircle" title="Tout est planifié">Aucune demande sans date.</Empty>}
              />
            )}
          </div>
        ) : (
          <div {...swipe}>
            {view !== "month" && (
              <div className="week-strip section" style={{ marginTop: 14 }}>
                <IconButton icon="back" label={view === "day" ? "Jour précédent" : "Semaine précédente"} onClick={prev} />
                {weekDays.map((d) => {
                  const count = days.get(d)?.filter((i) => i.status !== "cancelled").length ?? 0;
                  const dow = new Date(`${d}T12:00:00Z`).getUTCDay();
                  return (
                    <button
                      key={d}
                      type="button"
                      className={`day-cell ${d === today ? "today" : ""} ${dow === 0 || dow === 6 ? "weekend" : ""}`}
                      aria-pressed={view === "day" && d === date}
                      aria-label={`${formatDateLong(d)}, ${count} intervention${count > 1 ? "s" : ""}`}
                      onClick={() => go({ date: d, view: "day" })}
                    >
                      <span className="dow">{weekdayShort(d).replace(".", "")}</span>
                      <span className="num">{Number(d.slice(8))}</span>
                      <span className="dots">
                        {Array.from({ length: Math.min(count, 4) }, (_, i) => (
                          <i key={i} />
                        ))}
                      </span>
                    </button>
                  );
                })}
                <IconButton icon="chevron" label={view === "day" ? "Jour suivant" : "Semaine suivante"} onClick={next} />
              </div>
            )}

            {view === "month" && (
              <MonthGrid date={date} today={today} days={days} onPrev={prev} onNext={next} onPick={(d) => go({ date: d })} />
            )}

            {query.error && !query.data && (
              <div className="section">
                <ErrorState error={query.error} onRetry={() => void query.refetch()} />
              </div>
            )}
            {query.isPending && (
              <div className="section">
                <Skeleton />
              </div>
            )}

            {query.data && view === "day" && <DayList date={date} today={today} items={days.get(date) ?? []} />}
            {query.data && view === "month" && <DayList date={date} today={today} items={days.get(date) ?? []} />}
            {query.data && view === "week" && (
              <>
                {weekDays.map((d) => {
                  const items = days.get(d) ?? [];
                  const dow = new Date(`${d}T12:00:00Z`).getUTCDay();
                  if (!items.length && (dow === 0 || dow === 6)) return null;
                  return <DayList key={d} date={d} today={today} items={items} compact />;
                })}
              </>
            )}
          </div>
        )}
      </Page>
      <Fab to={`/interventions/new${unscheduledMode ? "" : `?date=${date}`}`} label="Intervention" />
    </>
  );
}

function DayList({ date, today, items, compact }: { date: string; today: string; items: Intervention[]; compact?: boolean }) {
  const label = formatDateLong(date);
  return (
    <section>
      <h2 className={`day-group-head ${date === today ? "today" : ""}`}>
        <span>{capitalize(label.replace(/ \d{4}$/, ""))}</span>
        {date === today && <span className="badge brand">Aujourd'hui</span>}
        <span className="muted">{items.length ? `${items.length}` : ""}</span>
      </h2>
      {items.length ? (
        <InterventionList items={items} />
      ) : compact ? (
        <p className="small muted" style={{ margin: "0 4px" }}>
          Rien de prévu.
        </p>
      ) : (
        <div className="card">
          <Empty icon="calendar" title="Aucune intervention ce jour-là" />
        </div>
      )}
    </section>
  );
}

function MonthGrid({
  date,
  today,
  days,
  onPrev,
  onNext,
  onPick
}: {
  date: string;
  today: string;
  days: Map<string, Intervention[]>;
  onPrev: () => void;
  onNext: () => void;
  onPick: (d: string) => void;
}) {
  const start = startOfWeek(startOfMonth(date));
  const end = addDays(startOfWeek(endOfMonth(date)), 6);
  const cells: string[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) cells.push(d);
  const month = date.slice(0, 7);

  return (
    <div className="card card-pad section" style={{ padding: 10 }}>
      <div className="row-between" style={{ marginBottom: 6 }}>
        <IconButton icon="back" label="Mois précédent" onClick={onPrev} />
        <strong style={{ textTransform: "capitalize" }}>{formatMonth(date)}</strong>
        <IconButton icon="chevron" label="Mois suivant" onClick={onNext} />
      </div>
      <div className="month-grid">
        {["lun", "mar", "mer", "jeu", "ven", "sam", "dim"].map((d) => (
          <div key={d} className="dow">
            {d}
          </div>
        ))}
        {cells.map((d) => {
          const items = (days.get(d) ?? []).filter((i) => i.status !== "cancelled");
          const late = items.some((i) => i.status === "planned" && d < today);
          return (
            <button
              key={d}
              type="button"
              className={`month-day ${d.slice(0, 7) !== month ? "out" : ""} ${d === today ? "today" : ""}`}
              aria-pressed={d === date}
              aria-label={`${formatDateLong(d)}, ${items.length} intervention${items.length > 1 ? "s" : ""}`}
              onClick={() => onPick(d)}
            >
              {Number(d.slice(8))}
              {items.length > 0 && <span className={`count ${late ? "warn" : ""}`}>{items.length}</span>}
            </button>
          );
        })}
      </div>
      <p className="tiny muted row" style={{ justifyContent: "center", marginTop: 6 }}>
        <Icon name="info" size={14} /> Touchez un jour pour voir le détail
      </p>
    </div>
  );
}
